import { type PropsWithChildren, useCallback } from "react";
import { useFormContext } from "react-hook-form";

import { usePaywallCheckoutContext } from "@/react/contexts/Paywalls/paywallCheckoutContext";
import type { CheckoutSession } from "@/react/types/Paywalls/Checkout/Session";

import { pushPurchaseEventFromOffer } from "../../../CheckoutWithSession/helpers/ga4PurchaseHelper";
import { useGoToNextStep } from "../../../CheckoutWithSession/hooks/useGoToNextStep";
import { isPaymentMethodRequired as checkIsPaymentMethodRequired } from "../../Attempts/helpers/isPaymentMethodRequired";
import { PaywallCheckoutAttemptsProvider } from "../../Attempts/PaywallCheckoutAttemptsProvider";
import type {
  CommunityProps,
  MemberProps,
  OnAddressChange,
  OnAddressChangeProps,
  OnComplete,
  OnCompleteProps,
  OnError,
  OnPaymentMethodChange,
  OnPaymentMethodChangeProps,
  OnProgress,
  OrderProps,
} from "../../Attempts/types";
import { useCheckoutSession } from "../../contexts/CheckoutSessionContext";
import { buildCompletedAttemptMoneyEvent } from "../../helpers/metaMoneyEventHelpers";
import { getPaywallPriceAmount } from "../../helpers/paywallPrices";
import { redirectToCheckoutConfirmation } from "../../hooks/paymentMethods/helpers";
import { useGetCouponApplication } from "../../hooks/useGetCouponApplication";
import { useHandleCheckoutError } from "../../hooks/useHandleCheckoutError";

export const AttemptsProvider = ({ children }: PropsWithChildren<unknown>) => {
  const { setValue, watch } = useFormContext();
  const { handleCheckoutError } = useHandleCheckoutError();
  const { session, ga4FiredStepIdsRef, refetchSession, markStepJustCompleted } =
    useCheckoutSession();
  const sessionId = session?.id;
  const {
    couponPreviewMutation,
    currentCommunity,
    currentCommunityMember,
    currentCommunityMemberPaymentProcessorSessionClientSecret:
      memberProcessorCustomerSessionClientSecret,
    currentCommunityProcessorPaymentMethodConfigurationId:
      communityProcessorPaymentMethodConfigurationId,
    currentUser,
    getSelectedPrice,
    isLinkPreferenceEnabled: doesCommunityProcessorSupportsLink,
    isMemberAddressRequired,
    isMemberWithSavedPaymentMethods: doesMemberHasDefaultPaymentMethod,
    memberBillingInfo,
    paywall,
    previewMutation,
    refreshCheckoutPreview,
    resetCheckoutPreviewError,
    processorId: communityProcessorId,
    processorKey: communityProcessorKey,
    sessionStepId,
    setIsCardInfoComplete,
    setIsMemberUsingSavedPaymentMethod,
    setIsProcessingPayment,
    setStripePaymentMethodType,
    checkoutConfirmationUrl,
  } = usePaywallCheckoutContext();
  const { goToNextStep } = useGoToNextStep();
  const formEmail = watch("email") as string | undefined;
  const formName = watch("name") as string | undefined;
  const selectedPrice = getSelectedPrice();
  const preview = previewMutation.data;
  const orderAmount = getPaywallPriceAmount(selectedPrice, preview);
  const orderCurrency = paywall.currency.code.toLowerCase();
  const orderCurrencyMinimumChargeAmount = paywall.currency.min_charge_amount;
  const paywallPriceId = selectedPrice.id;
  const communityProps: CommunityProps = {
    communityId: currentCommunity.id,
    communityProcessorId,
    communityProcessorKey,
    communityProcessorPaymentMethodConfigurationId,
    doesCommunityProcessorSupportsLink,
  };
  const memberProps: MemberProps = {
    doesMemberHasDefaultPaymentMethod,
    isMemberAddressComplete: memberBillingInfo?.address_complete || false,
    memberAddressCity: memberBillingInfo?.address_city,
    memberAddressCountry: memberBillingInfo?.address_country,
    memberAddressLine1: memberBillingInfo?.address_line1,
    memberAddressLine2: memberBillingInfo?.address_line2 || undefined,
    memberAddressPostalCode: memberBillingInfo?.address_postal_code,
    memberAddressState: memberBillingInfo?.address_state,
    memberEmail: currentUser?.email || formEmail,
    memberId: currentCommunityMember?.id,
    memberName: currentCommunityMember?.name || currentUser?.name || formName,
    memberProcessorCustomerSessionClientSecret,
    memberUserId: currentUser?.id,
  };
  const orderProps: OrderProps = {
    checkoutIntentToken: session?.checkout_intent_token,
    orderAmount,
    orderCurrency,
    orderCurrencyMinimumChargeAmount,
    orderPriceType: selectedPrice.price_type,
    paywallId: paywall.id,
    paywallPriceId,
    sessionId: session?.id,
    sessionStepId,
  };
  const couponApplication = useGetCouponApplication(
    selectedPrice.id,
    couponPreviewMutation,
  );
  const isPaymentMethodRequired = checkIsPaymentMethodRequired({
    doesOfferHaveTrial: selectedPrice.trial_enabled ?? false,
    doesOrderHaveFullForeverDiscount: couponApplication?.free_forever ?? false,
    doesTrialRequirePaymentMethod: paywall.trial_requires_card ?? false,
    doesTrialRequireUpfrontPayment:
      selectedPrice.trial_requires_upfront_payment ?? false,
  });

  const onAddressChange = useCallback<OnAddressChange>(
    (props: OnAddressChangeProps) => {
      setValue("name", props.name);
      setValue(
        "community_member_billing_info_attributes[address_line1]",
        props.line1,
      );
      setValue(
        "community_member_billing_info_attributes[address_line2]",
        props.line2,
      );
      setValue(
        "community_member_billing_info_attributes[address_city]",
        props.city,
      );
      setValue(
        "community_member_billing_info_attributes[address_state]",
        props.state,
      );
      setValue(
        "community_member_billing_info_attributes[address_postal_code]",
        props.postalCode,
      );
      setValue(
        "community_member_billing_info_attributes[address_country]",
        props.country,
      );
      setValue(
        "community_member_billing_info_address_attributes_complete",
        props.isComplete,
      );
    },
    [setValue],
  );
  const onPaymentMethodChange = useCallback<OnPaymentMethodChange>(
    (props: OnPaymentMethodChangeProps) => {
      setIsCardInfoComplete(props.isComplete);
      setStripePaymentMethodType(props.paymentMethodType);
      setIsMemberUsingSavedPaymentMethod(props.isDefaultPaymentMethod);
      // Also sync to form for useCardInfo (uses form's payment_method_type via watch())
      setValue("payment_method_type", props.paymentMethodType);
    },
    [
      setIsCardInfoComplete,
      setIsMemberUsingSavedPaymentMethod,
      setStripePaymentMethodType,
      setValue,
    ],
  );
  const onComplete: OnComplete = useCallback(
    async ({
      attemptId,
      attemptStatus,
      invoiceId,
      paymentIntentId,
      subscriptionId,
    }: OnCompleteProps) => {
      setIsProcessingPayment(false);
      const moneyEvent = buildCompletedAttemptMoneyEvent({
        attemptId,
        attemptStatus,
        currency: orderCurrency.toUpperCase(),
        currencySmallestUnit: paywall.currency.smallest_unit,
        hasConfiguredTrial: Boolean(selectedPrice.trial_enabled),
        value: selectedPrice.amount,
        upfrontPaymentAmount: selectedPrice.upfront_payment_amount ?? 0,
        installmentsCount: selectedPrice.installments_count ?? 0,
        priceType: selectedPrice.price_type,
        preview,
      });

      const trackPurchaseEvent = () => {
        if (!sessionId || !sessionStepId) return;
        // Track before webhook-driven session completion; the helper records the step in both dedup layers.
        pushPurchaseEventFromOffer({
          ga4Paywall: { id: paywall.id, display_name: paywall.display_name },
          selectedPrice,
          preview,
          subscriptionId,
          paymentIntentId,
          invoiceId,
          sessionId,
          stepId: sessionStepId,
          ga4FiredStepIdsRef,
        });
      };
      const redirectToConfirmation = () =>
        redirectToCheckoutConfirmation(
          {
            subscription_processor_id: subscriptionId,
            payment_intent_processor_id: paymentIntentId,
            invoice_processor_id: invoiceId,
          },
          checkoutConfirmationUrl,
        );

      // Suppress the already-purchased alert for this mount before refreshing.
      if (sessionStepId) {
        markStepJustCompleted(sessionStepId);
      }
      // Record the confirmed purchase using checkout context before refreshing.
      trackPurchaseEvent();

      // Refresh so goToNextStep() decides using fresh backend state.
      let freshSession: CheckoutSession | null = null;
      try {
        freshSession = await refetchSession();
      } catch {
        // Never route from the stale pre-submit session if refresh fails.
        freshSession = null;
      }

      const didNavigate = goToNextStep(freshSession, sessionStepId, moneyEvent);
      if (didNavigate) {
        return;
      }

      // Legacy fallback for non-session flows, and for a failed refresh above.
      redirectToConfirmation();
    },
    [
      checkoutConfirmationUrl,
      ga4FiredStepIdsRef,
      goToNextStep,
      markStepJustCompleted,
      paywall.display_name,
      paywall.id,
      preview,
      refetchSession,
      orderAmount,
      orderCurrency,
      selectedPrice,
      sessionId,
      sessionStepId,
      setIsProcessingPayment,
    ],
  );
  const onError = useCallback<OnError>(
    ({ error }) => {
      handleCheckoutError(error);
      setIsProcessingPayment(false);
    },
    [handleCheckoutError, setIsProcessingPayment],
  );
  const onCheckoutTermsChanged = useCallback(async () => {
    await refetchSession();
    try {
      await refreshCheckoutPreview();
    } catch (error: unknown) {
      resetCheckoutPreviewError(error);
      throw error;
    }
  }, [refetchSession, refreshCheckoutPreview, resetCheckoutPreviewError]);
  const onProgress = useCallback<OnProgress>(() => {
    setIsProcessingPayment(true);
  }, [setIsProcessingPayment]);

  return (
    <PaywallCheckoutAttemptsProvider
      {...communityProps}
      {...memberProps}
      {...orderProps}
      isMemberAddressRequired={isMemberAddressRequired}
      isPaymentMethodRequired={isPaymentMethodRequired}
      onAddressChange={onAddressChange}
      onCheckoutTermsChanged={onCheckoutTermsChanged}
      onComplete={onComplete}
      onError={onError}
      onPaymentMethodChange={onPaymentMethodChange}
      onProgress={onProgress}
    >
      {children}
    </PaywallCheckoutAttemptsProvider>
  );
};
